import Track from './Track.js';
import {transferPLocks} from './transferPLocks.js';
import InstallationTrack from './InstallationTrack.js';
import PlayableTrack from './PlayableTrack.js';
import SamplesStream from './SamplesStream.js';
import Pattern from 'Pattern';
import {convertOnset} from 'utils/extractGroove.js';
import StepData from 'StepData';
import Tone from 'tone';
import {recordStep} from 'operations/actions.js';
import ConvertableTrack from './ConvertableTrack.js';
import AuxEventable from './AuxEventable.js';
import FreezableTrack from './freeze/FreezableTrack.js';
import AuditionableTrack from './AuditionableTrack.js';
import {compose} from 'utils/compose.js';
import {copyGroups} from 'pattern/groups/stepGroups.js';

const PARAMS = ["Duration", "Transpose", "Velocity", "Chop", "Panning", "Slice"]; //, "Aux Event A", "Aux Event B"];

// MutableTrack is a sub-class of Track that allows it's underlying 
// patterns to be edited 
class MutableTrack extends compose(
    AuditionableTrack,
    PlayableTrack,
    InstallationTrack,
    FreezableTrack,
    ConvertableTrack,
    AuxEventable
)(Track.Track)
{
    constructor(trackNumber, sequencerEngine, mixSession, numberOfPatterns, update, subSequence) {
        super(trackNumber, sequencerEngine, mixSession, numberOfPatterns, update, subSequence);
	this.annotations = [];
        this.setupAuxEventParameters();
    }

    getStepDataParams() {
        return PARAMS;
    }

    // copies the whole track (samplers and all)
    // when ignoreId=true, it will not copy the ids over from the original track (i.e.
    // the parameter ids)
    getCopy(constr=(a,b,c,d,e,f) => new MutableTrack(a,b,c,d,e,f), sequencerEngine=this.sequencerEngine, ignoreId=true) {
        let trackData = this.trackData.getCopy(undefined, undefined, undefined, undefined, false);
        let soundIds = [].concat(this.soundIds);

        let track = constr(
            this.trackNumber,
            sequencerEngine,
            this.mixSession,
            this.patterns.length,
            this.update,
            sequencerEngine.currentSubSequence);

        track.stepGroups = this.stepGroups;

        // copy the patterns
        let patterns = this.patterns.map(pattern => pattern.getCopy(undefined,undefined,track));
        let currentPattern = this.currentPattern;
        let pattern = patterns[currentPattern];

        for (let i in patterns) {
            // go through the copied patterns
            let p = patterns[i];
            for (let j in p.stepBuckets) {
                // make sure the track value is set to the new copied track
                p.stepBuckets[j].track = track;
                for (let k in p.stepBuckets[j].steps) {
                    // same for steps, make sure each step references this new
                    // track
                    p.stepBuckets[j].steps[k].track = track;
                }
            }
            // finally make sure the track in pattern references the track
            p.track = track;

        }
        if (this.sample) {
            track.sample = this.sample.getCopy();
        }

        if (track.lfosTrack) {
            track.lfosTrack.lfos.forEach(
                (lfo, i) => lfo.lfo.fromJson(
                    this.lfosTrack.lfos[i].getJson(),
                    ignoreId
                ));
        }

        if (track.envelopesTrack) {
            track.envelopesTrack.envelopes.forEach(
                (env, i) => env.envelope.fromJson(
                    this.envelopesTrack.envelopes[i].getJson(),
                    ignoreId
                ));
        }

        track.name = this.name;
        track.videoId = this.videoId;
        track.effectsChains[0].dispose();
        track.effectsChains = this.effectsChains.map(chain => chain.getCopy(track, sequencerEngine, ignoreId));
        track.midiEffects = this.midiEffects.getCopy(track, ignoreId);
        track.liveEffects = this.liveEffects.getCopy(track, ignoreId);
        track.trackData = trackData;
        track.patterns = patterns;
        track.currentPattern = currentPattern;
        track.pattern = pattern;
        track.soundIds = soundIds;
        track.gated = this.gated;

        // now that the effects are accounted for, we need to transfer
        // any p-locks that were in the original pattern to the new track
        // The parameter references should reference the newly copied parameters
        for (let i=0; i <  patterns.length; i++) {
            // we need to transfer any pLocks
            transferPLocks(this, track, this.patterns[i], patterns[i]);
        }

        let connectedEnvelopes = this.connectedEnvelopes
            .filter(x => x.envelope)
            .map(x => x.getJson());
        for (let connection of connectedEnvelopes) {
            let envelope = sequencerEngine.envelopes[connection.envelope];
            track.connectToEnvelope(
                envelope,
                connection.mode);
        }

        let samplers = {};
        for (var key in this.samplers) {
            samplers[key] = this.samplers[key].getCopy(
                (s) => track.onNewSampler(track, s),
                track);
        }

        track.samplers = samplers;
        track.updateSteps(true);

        track.setSends(sequencerEngine.sends);
        for (let i in track.sendNodeVolumeParameters) {
            if (this.sendNodeVolumeParameters[i]) {
                track.sendNodeVolumeParameters[i].copyFrom(
                    this.sendNodeVolumeParameters[i], sequencerEngine);
            }
        }

        track.volumeParameter.copyFrom(
            this.volumeParameter, sequencerEngine);

        track.isFrozen = this.isFrozen;

        if (track.isFrozen) {
            let frozenJson = this.getFreezeJsonSync();
            track.loadFreezeJsonSync(frozenJson);
        }

        if (this.samplesStream) {
            track.samplesStream = this.samplesStream.getCopy(track.sample);
        } else {
            track.samplesStream =
                new SamplesStream(track.sample, this.sequencerEngine);
        }

        track.presetName = this.presetName;
        return track;
    }

    addNotes(notes, sampleTempo=this.sequencerEngine.bpm) {
        const middleC = 60;

        let durationTime = Tone.Time('16n').toMilliseconds();
        notes = notes.map(
            note => {
                let velocity = note.velocity / 127.0;
                return {
                    transpose: (note.pitch - middleC),
                    startTime: 1000 * note.startTime,
                    velocity: velocity,
                    duration: (sampleTempo / this.sequencerEngine.bpm) *
                        (1000 * (note.endTime - note.startTime)) / durationTime
            }});

        let stepTime = Tone.Time('32n').toMilliseconds();

        let lastStep = -1;
        for (let i in notes) {
            let convertedOnset = convertOnset(
                notes[i].startTime, sampleTempo, this.sequencerEngine.bpm);
            notes[i].step = convertedOnset.stepNumber;

            // we are doing this unquantized so we figure out the difference;
            notes[i].position = convertedOnset.offset;

            if (notes[i].step > lastStep) {
                lastStep = notes[i].step;
            }
        }

        while (lastStep >= this.pattern.length()) {
            this.sequencerEngine.doubleTrackLength(this.trackNumber);
        }

        let pattern = this.getEasyPattern();
        notes.forEach(note => pattern.addStep(this.getStepDataForNote(note), note.step, Track.baseResolution, note.position, false, true));
        pattern.update(false);
        this.updateSteps();
    }

    getStepDataForNote(note) {
        let stepData = new StepData(this.sequencerEngine, undefined, undefined, this);
        stepData.on = true;
        stepData.transpose = note.transpose;
        stepData.duration = note.duration;
        stepData.velocity = note.velocity;
        return stepData;
    }

    // turns a step on or off, using the given resolution to choose
    // the step to be operated on.
    toggleStep(step, resolution=this.store.getState().sequencer.resolution, transpose=undefined) {
        this.pattern.toggleStep(
            Track.baseResolution.transformStepAtResolution(step, resolution),
            resolution,
            transpose,
            this.trackData.transpose === transpose);

        this.pattern.syncBuckets();
        this.updateSteps();
    }

    getStep(stepNumber) {
        let step = this.pattern.getAnyStep(stepNumber % this.pattern.length());
        return step;
    }

    // records a hit at specific step/slice, saving it to the data
    recordHit(stepNumber, resolution, slice, duration, transpose, velocity=1, release=null) {
        resolution = resolution || Track.baseResolution;
        let stepData = new StepData(undefined, undefined, undefined, this);
        stepData.on = true;
        stepData.slice = slice;
        stepData.duration = duration;
        stepData.transpose = transpose;
        stepData.velocity = velocity;
        stepData.release = release;
        
        if (this.currentPattern === undefined) {
            // create a new pattern with this step
            let pattern = new Pattern(this.sequencerEngine, this); 
            let startStep = stepNumber - (stepNumber % resolution.transformStepAtResolution2(16, Track.baseResolution));
            pattern.startStep = Track.baseResolution.transformStepAtResolution2(startStep, resolution);
            this.patterns.push(pattern);
            this.switchToPattern(this.patterns.length - 1);
            this.updateSteps();
        } 

        if (this.pattern.startStep !== undefined) {
            stepNumber =
                stepNumber -
                resolution.transformStepAtResolution2(this.pattern.startStep, Track.baseResolution);
        }

        // keep track that we are "recording"
        this.pattern.isRecording = true;
        
        this.sequencerEngine.operationManager.dispatch(
            recordStep(this, stepData, stepNumber, resolution, this.state.sequencer.displayResolution));
    }

    syncBuckets(resolution) {
        this.pattern.syncBuckets(resolution.isTriplet);
        this.pattern.clearCache();
    }

    // records a hit at specific time within a  step/slice,
    // saving it to the pattern. Doesn't take in a resolution
    // because we assume default 1/32 resolution in unquantized mode
    recordUnquantizedHit(stepNumber, slice, duration, bucketKey, transpose, velocity=1, release=null) {
        let resolution = Track.baseResolution;
        let stepData = new StepData();
        stepData.on = true;
        stepData.slice = slice;
        stepData.velocity = velocity;
        stepData.duration = duration;
        stepData.transpose = transpose;
        stepData.release = release;
        let _stepNumber = (stepNumber + bucketKey) % this.pattern.length(resolution);
        this.sequencerEngine.operationManager.dispatch(
            recordStep(
                this,
                stepData,
                Math.max(0, _stepNumber),
                resolution,
                this.state.sequencer.displayResolution,
                false
            ),
        );
    }

    selectSlice(stepNumber, sliceNumber) {
        var maxSlices = -16;
        for (var i=0; i < this.soundIds.length; i++) {
            let slices = this.samplers[this.soundIds[i]].getNumberOfSlices();
            if (maxSlices < slices) {
                maxSlices = slices;
            }
        }

        sliceNumber = sliceNumber % maxSlices;
        
        let bucket = this.pattern.stepBuckets[stepNumber];
        for (var key in bucket.steps) {
            bucket.steps[key].slice = sliceNumber;
        }
    }
}
export default MutableTrack;
